import { Image, Spin, Tag } from "antd";
import { useMemo } from "react";
import { EmptyState } from "@/shared/components/EmptyState";
import { resolveImageUrl } from "@/shared/utils/resolveImageUrl";
import { useDailyAttendanceByStaff } from "../hooks/useDailyAttendance";
import type { AttendancePhotoItem, AttendanceTableProps } from "../types";
import { buildPhotoGalleryItems } from "../utils/attendance.utils";

function TerritoryTag({ insideTerritory }: { insideTerritory: boolean | null }) {
	if (insideTerritory === true) {
		return <Tag color="success">Hudud ichida</Tag>;
	}
	if (insideTerritory === false) {
		return <Tag color="error">Hudud tashqarisida</Tag>;
	}
	return null;
}

function PhotoCard({ photo }: { photo: AttendancePhotoItem }) {
	return (
		<div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
			<Image
				src={resolveImageUrl(photo.photoUrl)}
				alt={`${photo.staffName} — ${photo.dateLabel}`}
				width="100%"
				height={160}
				style={{ objectFit: "cover" }}
			/>
			<div className="p-2">
				<div className="truncate font-medium text-sm" title={photo.staffName}>
					{photo.staffName}
				</div>
				<div className="text-gray-500 text-xs">
					{photo.dateLabel} · {photo.time}
				</div>
				<div className="mt-1 flex flex-wrap gap-1">
					<Tag
						color={
							photo.period === "MORNING" ? "green" : photo.period === "NOON" ? "gold" : "volcano"
						}
					>
						{photo.period === "MORNING"
							? "Ertalab"
							: photo.period === "NOON"
								? "Kunduz"
								: "Kechqurun"}
					</Tag>
					<TerritoryTag insideTerritory={photo.insideTerritory} />
				</div>
			</div>
		</div>
	);
}

/** Grid of the day's attendance photos (one per filled period slot) with lightbox preview */
export function AttendancePhotoGallery({
	organizationId,
	startDate,
	endDate,
}: Omit<AttendanceTableProps, "organizationName">) {
	const { data: byStaffItems, isLoading } = useDailyAttendanceByStaff(
		organizationId,
		startDate,
		endDate
	);

	const photos = useMemo(() => {
		if (!byStaffItems) {
			return [];
		}
		return buildPhotoGalleryItems(byStaffItems);
	}, [byStaffItems]);

	if (isLoading) {
		return (
			<div className="flex justify-center rounded-lg bg-white py-12">
				<Spin />
			</div>
		);
	}

	if (photos.length === 0) {
		return (
			<div className="rounded-lg bg-white">
				<EmptyState description="Tanlangan davr uchun davomat fotolari topilmadi" />
			</div>
		);
	}

	return (
		<div className="rounded-lg bg-white p-4">
			<Image.PreviewGroup>
				<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
					{photos.map((photo) => (
						<PhotoCard key={photo.key} photo={photo} />
					))}
				</div>
			</Image.PreviewGroup>
		</div>
	);
}
